import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { CheckCircle2, MessageCircle, Users, AlertTriangle, Star, Crown } from "lucide-react";

const CATALOG_FALLBACK = {
  plans: [
    { key: "mentor_core", name: "Mentor Core", price_brl_month: 147, capacity: 20, positioning: "Transforme seu método em uma experiência.", badge: null },
    { key: "mentor_pro", name: "Mentor Pro", price_brl_month: 297, capacity: 75, positioning: "Acompanhe mais alunos sem perder o controle.", badge: "Mais escolhido" },
    { key: "mentor_scale", name: "Mentor Scale", price_brl_month: 597, capacity: 250, positioning: "Transforme sua mentoria em uma operação.", badge: null },
  ],
  sales_whatsapp: "5521970396046",
  custom_note: "Precisa de mais de 250 acessos? Fale com a gente.",
};

const STATUS_COPY = {
  pending: { color: "bg-yellow-500/20 text-yellow-300 border-yellow-500/40", label: "Aguardando aprovação do Dono" },
  active: { color: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40", label: "Ativo" },
  blocked: { color: "bg-red-500/20 text-red-300 border-red-500/40", label: "Bloqueado" },
  rejected: { color: "bg-red-500/20 text-red-300 border-red-500/40", label: "Rejeitado" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return "—"; }
}

export default function MentorPlansCard() {
  const [catalog, setCatalog] = useState(CATALOG_FALLBACK);
  const [profile, setProfile] = useState(null);
  const [requesting, setRequesting] = useState(null);

  const load = async () => {
    try {
      const [cat, me] = await Promise.all([
        api.get("/api/mentor/plans"),
        api.get("/api/mentor/me"),
      ]);
      if (cat.data?.plans?.length) setCatalog(cat.data);
      setProfile(me.data);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[mentor-plans] load failed", err?.message);
    }
  };

  useEffect(() => { load(); }, []);

  const requestPlan = async (planKey) => {
    setRequesting(planKey);
    try {
      const res = await api.post("/api/mentor/request-plan", { plan_key: planKey });
      setProfile((p) => ({ ...(p || {}), requested_plan: planKey }));
      if (res.data?.whatsapp_url) {
        window.open(res.data.whatsapp_url, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      alert(err?.response?.data?.detail || "Não foi possível registrar o interesse.");
    } finally {
      setRequesting(null);
    }
  };

  const currentPlanKey = profile?.current_plan;
  const status = profile?.mentor_status || "pending";
  const statusMeta = STATUS_COPY[status] || STATUS_COPY.pending;
  const usage = profile?.usage_active_students ?? 0;
  const capacity = profile?.capacity;
  const nearCapacity = profile?.near_capacity;
  const atCapacity = profile?.at_capacity;
  const usagePct = capacity ? Math.min(100, Math.round((usage / capacity) * 100)) : 0;
  const whatsapp = catalog.sales_whatsapp || CATALOG_FALLBACK.sales_whatsapp;

  return (
    <div data-testid="mentor-plans-card" className="space-y-4">
      {/* Status banner */}
      <div className="bg-[#14161C] border border-[#282D3D] rounded-xl p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2">
            <Crown className="w-5 h-5 text-[#FF6B00]" />
            <h3 className="text-base font-bold text-white">Meu Plano de Mentor</h3>
          </div>
          <span data-testid="mentor-status-badge" className={`text-[11px] px-2.5 py-1 rounded-full font-bold border ${statusMeta.color}`}>
            {statusMeta.label}
          </span>
        </div>

        {status === "pending" && (
          <p className="text-xs text-yellow-200/80 mt-3 leading-relaxed">
            Sua conta de mentor foi criada e está aguardando aprovação do Dono da plataforma.
            Escolha um plano abaixo e envie sua solicitação via WhatsApp para ativar seu acesso.
          </p>
        )}
        {status === "blocked" && (
          <p className="text-xs text-red-300/90 mt-3">
            Sua conta está bloqueada. Fale com a equipe pelo WhatsApp para reativar.
          </p>
        )}

        {/* Current plan usage */}
        {currentPlanKey && (
          <div className="mt-4 p-4 bg-[#1A1D26] border border-[#282D3D] rounded-lg space-y-2">
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-[#FF6B00]" />
                <span className="text-gray-300">Plano atual: <strong className="text-white">{profile?.plan?.name || currentPlanKey}</strong></span>
              </div>
              <span className="text-gray-400">Renova em <strong className="text-white">{fmtDate(profile?.plan_expires_at)}</strong></span>
            </div>
            {capacity !== null && capacity !== undefined && (
              <>
                <div className="flex justify-between text-[11px] text-gray-400">
                  <span>Uso do plano</span>
                  <span className="font-mono text-white">{usage} / {capacity} acessos ativos</span>
                </div>
                <div className="w-full h-2 bg-[#0F1218] rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all ${atCapacity ? "bg-red-500" : nearCapacity ? "bg-yellow-400" : "bg-emerald-400"}`}
                    style={{ width: `${usagePct}%` }}
                  />
                </div>
                {atCapacity && (
                  <div className="flex items-center gap-1.5 text-[11px] text-red-300 mt-1">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    <span>Você atingiu o limite do plano. Faça upgrade para liberar mais acessos.</span>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Plans grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {catalog.plans.map((plan) => {
          const isCurrent = plan.key === currentPlanKey;
          const isRequested = plan.key === profile?.requested_plan && !isCurrent;
          const isHighlighted = !!plan.badge;
          return (
            <div
              key={plan.key}
              data-testid={`mentor-plan-${plan.key}`}
              className={`bg-[#14161C] border rounded-xl p-4 relative transition-all ${
                isCurrent
                  ? "border-emerald-500/60 shadow-[0_0_18px_rgba(16,185,129,0.15)]"
                  : isHighlighted
                    ? "border-[#FF6B00]/60 shadow-[0_0_20px_rgba(255,107,0,0.15)]"
                    : "border-[#282D3D]"
              }`}
            >
              {plan.badge && !isCurrent && (
                <span className="absolute -top-2 left-3 bg-[#FF6B00] text-black text-[10px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded flex items-center gap-1">
                  <Star className="w-3 h-3 fill-black" /> {plan.badge}
                </span>
              )}
              {isCurrent && (
                <span className="absolute -top-2 left-3 bg-emerald-500 text-black text-[10px] font-extrabold uppercase tracking-wider px-2 py-0.5 rounded">
                  Ativo
                </span>
              )}
              <h4 className="text-base font-extrabold text-white">{plan.name}</h4>
              <div className="mt-1 text-xs text-gray-400 min-h-[32px]">{plan.positioning}</div>
              <div className="mt-3">
                <span className="text-2xl font-extrabold text-white">R$ {plan.price_brl_month}</span>
                <span className="text-xs text-gray-400"> /mês</span>
              </div>
              <div className="mt-2 text-xs text-gray-300 flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                Até <strong className="text-white">{plan.capacity} acessos ativos</strong>
              </div>
              <button
                data-testid={`mentor-plan-cta-${plan.key}`}
                onClick={() => requestPlan(plan.key)}
                disabled={requesting === plan.key}
                className={`mt-4 w-full py-2 rounded-lg font-extrabold text-xs uppercase tracking-wider flex items-center justify-center gap-2 transition-all disabled:opacity-60 ${
                  isCurrent
                    ? "bg-[#1F2330] text-gray-300 border border-[#3E4760]"
                    : "bg-[#FF6B00] hover:bg-[#FF8533] text-black shadow-[0_0_15px_rgba(255,107,0,0.35)]"
                }`}
              >
                <MessageCircle className="w-3.5 h-3.5" />
                {isCurrent
                  ? "Renovar via WhatsApp"
                  : isRequested
                    ? "Reabrir conversa"
                    : "Quero este plano"}
              </button>
            </div>
          );
        })}
      </div>

      {/* Custom / >250 note */}
      <div className="bg-[#14161C] border border-[#282D3D] rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="text-xs text-gray-300">
          <strong className="text-white">{catalog.custom_note || "Precisa de mais de 250 acessos?"}</strong>
        </div>
        <button
          data-testid="mentor-plan-cta-custom"
          onClick={() => requestPlan("custom_over_250")}
          className="px-4 py-2 rounded-lg bg-[#1F2330] hover:bg-[#282F42] border border-[#3E4760] text-white font-bold text-xs flex items-center gap-2"
        >
          <MessageCircle className="w-3.5 h-3.5" />
          Falar com o time
        </button>
      </div>

      <p className="text-[10px] text-center text-gray-500">
        Pagamentos e cobrança são feitos externamente via WhatsApp comercial (+{whatsapp}).
      </p>
    </div>
  );
}
